import { useCallback, useState } from 'react';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import { Offcanvas } from 'react-bootstrap';
import { Flex, View, DialogContainer, Dialog, Content, Header } from '@adobe/react-spectrum';
import { FilterIcon, TaskIcon } from '../../../../components/protected/Icons/IconsLib';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../state/store';
import UsersFilterComponent from '../../../../components/protected/layout-components/FilterComponent/UsersFilter/UsersFilterComponent';
import { useTranslation } from 'react-i18next';
import styles from './filter_component.module.css';
import EventWorktypeFilterComponent from '../../calendar/components/EventWorkTypefilter';
import TypeOfWorkFilterComponent from './TypeOfWorkfilter';
import { useDeviceDetection } from '../../../../hooks/UseDeviceDetection';
import StandardStatusFilterDropdown from './StandardStatusFilterDropdown';

interface IFilterComponentProps {
  onFilterChange: (filterParams: {}) => void;
  filterParams: any;
  filterStyle?: any;
  priceList?: any[];
}

const FilterComponent: React.FC<IFilterComponentProps> = ({ onFilterChange, filterParams, filterStyle, priceList }) => {
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const { t } = useTranslation('task');
  const _t = (key: string) => t(`task_filters.${key}`, { ns: 'task' });
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const [show, setShow] = useState(false);
  const [selectedView, setSelectedView] = useState<string>(filterParams?.FILTER.COL.find((col: any) => col.name === 'view')?.value || 'todo_tasks');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [clearFilters, setClearFilters] = useState<boolean>(false);

  const handleClose = () => setShow(false);
  const handleShow = () => {
    setShow(true);
    setSelectedView(filterParams?.FILTER.COL.find((col: any) => col.name === 'view')?.value || 'todo_tasks');
  };

  const removeFilterParam = (name: string) => {
    filterParams.FILTER.COL = filterParams.FILTER.COL.filter((col: any) => col.name !== name);
    onFilterChange(filterParams);
  };

  const createParams = useCallback(
    async (filterTypeName: any, filterValue: any) => {
      const filterValueStr =
        filterValue instanceof Set
          ? Array.from(filterValue).join(",")
          : Array.isArray(filterValue)
            ? filterValue.join(",")
            : String(filterValue ?? "");
      if (
        (filterTypeName == "type" && filterValueStr == "All types") ||
        (filterTypeName == "customState" && filterValueStr == "All states") ||
        (filterTypeName == "user" && filterValueStr == "All users") ||
        (filterTypeName == "view" && filterValueStr == "todo_tasks") ||
        (filterTypeName == "standardStatus" && filterValueStr == "0")
      ) {
        removeFilterParam(filterTypeName);
        return;
      }
      handleFilterType(filterTypeName, filterValueStr, filterValue);
    },
    [selectedApp]
  );

  const updateFilterParams = (name: string, value: any) => {
    let updatedArray = filterParams.FILTER.COL.map((obj: any) =>
      obj.name === name ? { ...obj, value } : obj
    );
    const hasNameSearch = filterParams.FILTER.COL.some(
      (obj: any) => obj.name === name
    );

    if (!hasNameSearch) {
      updatedArray.push({ name, value });
    }
    filterParams.FILTER.COL = updatedArray;
  };
  
  const handleFilterType = (
    filterTypeName: any,
    filterValueStr: any,
    filterValue: any
  ) => {
    switch (filterTypeName) {
      case "type":
        updateFilterParams("type", filterValueStr);
        break;
      case "customState":
        updateFilterParams("customState", filterValue);
        break;
      case "user":
        updateFilterParams("user", filterValue);
        break;
        case "view":
          updateFilterParams("view",filterValue)
          break;
      case "standardStatus":
        updateFilterParams("standardStatus", filterValueStr);
        break;
      default:
        break;
    }
    //console.log('filterParams', filterParams);
    filterParams.FILTER.COL = filterParams.FILTER.COL.filter(
      (col: any) => col.value !== ""
    );
    onFilterChange(filterParams);
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    setTimeout(() => setClearFilters(false), 0);
  };

  const shouldUseMobileLayout = deviceInfo.shouldUseMobileLayout();
  const isMobileOrSmallScreen =
    shouldUseMobileLayout || deviceInfo.isIPad || deviceInfo.isTablet;

  return isMobileOrSmallScreen ? (
    <View position={'relative'} width={'100%'} height={'64px'} UNSAFE_className={styles.mobile_header_parent}>
      <Flex direction={'column'} position={'absolute'} top={'calc(50% - 16px)'} left={'50px'} align-items={'start'} justifyContent={'start'}>
        <View position={'relative'} UNSAFE_className={styles.mobile_header_text_1}>
          {_t("header_text")}
        </View>
      </Flex>
      <View position={'absolute'} top={'calc(50% - 12px)'} left={'16px'} width={'24px'} height={'24px'} overflow={'hidden'}>
        <span onClick={handleShow} className="me-2">
          <i className="bi bi-list"></i>
        </span>
        <Offcanvas show={show} onHide={handleClose} placement="start">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title></Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Flex
              direction={'column'}
              position={'absolute'}
              top={'50px'}
              left={'15px'}
              width={'240px'}
              alignItems={'start'}
              justifyContent={'start'}
              gap={'5px'}
              UNSAFE_className={styles.views_menu_container}
            >
              <Flex direction={'row'} UNSAFE_className={styles.views_menu_heading} alignSelf={'stretch'} alignItems={'center'} justifyContent={'start'}>
                <View position={'relative'} UNSAFE_className={styles.menu_heading_text}>
                  {_t('task_views')}
                </View>
              </Flex>
              <Flex direction={'column'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} gap={'4px'} UNSAFE_className={styles.filter_views_parent}>
                <a
            onClick={() => {
              setSelectedView('todo_tasks');
              createParams('view', 'todo_tasks');
            }}
          >
            <Flex
              direction={'row'}
              height={'36px'}
              alignItems={'center'}
              justifyContent={'center'}
              UNSAFE_className={selectedView === 'todo_tasks' ? styles.filter_views_container_selected : styles.filter_views_container}
            >
              <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                <Flex position={'relative'} flex={1} UNSAFE_className={styles.filter_views_text}>
                  {_t('task_to_do')}
                </Flex>
              </Flex>
            </Flex>
          </a>
          <a
            onClick={() => {
              setSelectedView('task_to_check');
              createParams('view', 'task_to_check');
            }}
          >
            <Flex UNSAFE_className={selectedView === 'task_to_check' ? styles.filter_views_container_selected : styles.filter_views_container}>
              <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                <Flex position={'relative'} flex={1} UNSAFE_className={styles.filter_views_text}>
                  {_t('task_to_check')}
                </Flex>
              </Flex>
            </Flex>
          </a>
          <a
            onClick={() => {
              setSelectedView('custom_tasks');
              createParams('view', 'custom_tasks');
            }}
          >
            <Flex UNSAFE_className={selectedView === 'custom_tasks' ? styles.filter_views_container_selected : styles.filter_views_container}>
              <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                <Flex position={'relative'} flex={1} UNSAFE_className={styles.filter_views_text}>
                  {_t('custom_task')}
                </Flex>
              </Flex>
            </Flex>
          </a>
              </Flex>
            </Flex>
            {/* <View position={'relative'} top={'85%'} width={'100%'} height={'1px'} UNSAFE_className={styles.separator}>
            <Flex direction={'column'} position={'absolute'} width={'240px'} alignItems={'start'} justifyContent={'start'} gap={'5px'} UNSAFE_className={styles.views_menu_container}>
              <Flex direction={'column'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.button_group_parent}>
                <Flex position={'relative'} width={'266px'} height={'56px'} flexShrink={0} UNSAFE_className={styles.button_group_wrapper}>
                  <Flex direction={'row'} position={'absolute'} top={'calc(50% - 10px)'} left={'14px'} alignItems={'center'} justifyContent={'center'} UNSAFE_className={styles.btn_style}>
                    <Link
                      isQuiet
                      onPress={() => {
                      }}
                    >
                      <>
                        <Plus fontSize={'25px'} />
                        {t('add_view', { ns: 'layout_components' })}
                      </>
                    </Link>
                  </Flex>
                </Flex>
                <Flex position={'relative'} width={'266px'} height={'56px'} flexShrink={0} UNSAFE_className={styles.button_group_wrapper}>
                  <Flex direction={'row'} position={'absolute'} gap={'8px'} top={'calc(50% - 10px)'} left={'14px'} alignItems={'center'} justifyContent={'center'} UNSAFE_className={styles.btn_style}>
                    <Link
                      isQuiet
                      onPress={() => {
                      }}
                    >
                      <>
                        <Pen />
                        {t('edit_view', { ns: 'layout_components' })}
                      </>
                    </Link>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </View> */}
          </Offcanvas.Body>
        </Offcanvas>
      </View>

      <View position={'absolute'} top={'calc(50% - 12px)'} left={'calc(100% - 40px)'} width={'24px'} height={'24px'} overflow={'hidden'}>
        <div onClick={() => setIsOpen(true)}>
          <FilterIcon size={20} />
        </div>

        <DialogContainer
          isDismissable
          onDismiss={() => {
            setIsOpen(false);
          }}
        >
          {isOpen && (
            <Dialog size="L">
              <Content>
                <Header>
                  <Flex position={'relative'} direction={'row'} justifyContent={'center'} alignContent={'center'} width={'100%'}>
                    <View position={'relative'} UNSAFE_className={styles.popup_header_text}>
                      {t('filters', { ns: 'layout_components' })}
                    </View>
                  </Flex>
                </Header>
                <View width={'100%'} position={'relative'} height={'100vh'}>
                  <Flex
                    direction={'column'}
                    position={'absolute'}
                    width={'95%'}
                    top={'30px'}
                    left={'16px'}
                    alignItems={'start'}
                    justifyContent={'start'}
                    gap={'24px'}
                    UNSAFE_className={styles.popup_header_body_parent}
                  >
                    <Flex direction={'column'} width={'100%'} alignItems={'start'} justifyContent={'start'} gap={'12px'} UNSAFE_className={styles.popup_header_body}>
                      <StandardStatusFilterDropdown createParams={createParams} filterParams={filterParams} />
                      <UsersFilterComponent createParams={createParams} filterParams={filterParams} />
                      <TypeOfWorkFilterComponent createParams={createParams} filterParams={filterParams} />
                    </Flex>
                  </Flex>
                </View>
              </Content>
            </Dialog>
          )}
        </DialogContainer>
      </View>
    </View>
  ) : (
    <View width={'100%'} position={'relative'} backgroundColor={'static-white'} UNSAFE_className={styles.filter_menu_parent} UNSAFE_style={filterStyle}>
      <Flex direction={'column'} position={'absolute'} top={'24px'} left={'0px'} width={'240px'} alignItems={'start'} justifyContent={'start'} gap={'5px'} UNSAFE_className={styles.views_menu_container}>
        {/* <Flex direction={'row'} UNSAFE_className={styles.views_menu_heading} alignSelf={'stretch'} alignItems={'center'} justifyContent={'start'}>
          <View position={'relative'} UNSAFE_className={styles.menu_heading_text}>
            {_t('event_views')}
          </View>
        </Flex> */}
        <Flex direction={'column'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} gap={'4px'} UNSAFE_className={styles.filter_views_parent}>
          <a
            onClick={() => {
              setSelectedView('todo_tasks');
              createParams('view', 'todo_tasks');
            }}
          >
            <Flex
              direction={'row'}
              height={'36px'}
              alignItems={'center'}
              justifyContent={'center'}
              UNSAFE_className={selectedView === 'todo_tasks' ? styles.filter_views_container_selected : styles.filter_views_container}
            >
              <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                <Flex position={'relative'} flex={1} UNSAFE_className={styles.filter_views_text}>
                  {_t('task_to_do')}
                </Flex>
              </Flex>
            </Flex>
          </a>
          <a
            onClick={() => {
              setSelectedView('task_to_check');
              createParams('view', 'task_to_check');
            }}
          >
            <Flex UNSAFE_className={selectedView === 'task_to_check' ? styles.filter_views_container_selected : styles.filter_views_container}>
              <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                <Flex position={'relative'} flex={1} UNSAFE_className={styles.filter_views_text}>
                  {_t('task_to_check')}
                </Flex>
              </Flex>
            </Flex>
          </a>
          <a
            onClick={() => {
              setSelectedView('custom_tasks');
              createParams('view', 'custom_tasks');
            }}
          >
            <Flex UNSAFE_className={selectedView === 'custom_tasks' ? styles.filter_views_container_selected : styles.filter_views_container}>
              <Flex direction={'row'} flex={1} alignItems={'center'} justifyContent={'start'}>
                <Flex position={'relative'} flex={1} UNSAFE_className={styles.filter_views_text}>
                  {_t('custom_task')}
                </Flex>
              </Flex>
            </Flex>
          </a>
        </Flex>
      </Flex>
      <View position={'relative'} top={'140px'} width={'100%'} height={'1px'} UNSAFE_className={styles.separator}></View>
      <Flex
        direction={'column'}
        position={'absolute'}
        top={'155px'}
        left={'0px'}
        width={'240px'}
        alignItems={'start'}
        justifyContent={'start'}
        gap={'5px'}
        UNSAFE_className={styles.filters_menu_container}
      >
        <Flex direction={'row'} alignSelf={'stretch'} alignItems={'start'} justifyContent={'start'} UNSAFE_className={styles.filters_menu_wrapper}>
          <View position={'relative'} UNSAFE_className={styles.menu_heading_text}>
            {t('filters', { ns: 'layout_components' })}
          </View>
        </Flex>
        <StandardStatusFilterDropdown createParams={createParams} filterParams={filterParams} />
        <UsersFilterComponent createParams={createParams} filterParams={filterParams} />
        <TypeOfWorkFilterComponent createParams={createParams} filterParams={filterParams} />
      </Flex>
    </View>
  );
};

export default FilterComponent;
